import { z } from "zod";
/**
 * The catalogue's closed sets, on the wire.
 *
 * ── WHY THESE ARE RESTATED RATHER THAN IMPORTED ─────────────────────────────
 * `@repo/catalog` already declares every one of these unions in
 * `src/core/vocabulary.ts`, and importing them from here is forbidden by the
 * architecture audit's rule 5: `packages/shared` may not import a module
 * package. That direction is what keeps the wire contract usable by the mobile
 * app, which must never pull a server-side module into its bundle.
 *
 * The duplication is therefore deliberate, and it is checked rather than
 * trusted: `apps/api`'s route handlers map a module value into one of these
 * schemas, so a value the module produces and this file does not name fails
 * the response parse in the pool tests. See
 * `apps/api/src/__tests__/pool/catalogue-routes.test.ts`.
 */
/** 2,275 strength · 388 stretching · 338 aerobic. */
export const exerciseCategory = z.enum(["strength", "stretching", "aerobic"]);
export const muscleBodyRegion = z.enum(["upper_body", "lower_body", "core"]);
export const muscleMovementType = z.enum(["push", "pull", "legs", "core"]);
/**
 * What a muscle does in an exercise.
 *
 * The detail screen shows both and distinguishes them (`ALV` / `SIN`), and the
 * filters treat them asymmetrically on purpose: a region or movement-type
 * filter accepts only `target`, while a specific-muscle filter accepts either.
 */
export const muscleRole = z.enum(["target", "synergist"]);
/**
 * How a logged load reads for this exercise (`D196`, `catalog_exercise.load_mode`).
 *
 * ── WHY "20 kg" IS NOT ONE NUMBER ───────────────────────────────────────────
 * - `total` — the load moved is the number logged: a barbell, a machine stack.
 * - `per_hand` — two implements, ONE of them logged: a dumbbell press at
 *   "20 kg" moves 40. Volume and e1RM double it.
 * - `bodyweight_plus` — the body plus what is logged: a weighted dip.
 * - `assisted` — the body MINUS what is logged: an assisted pull-up machine,
 *   where a bigger number is an easier set.
 *
 * Absent or `null` means `total`, which is how every set was read before the
 * column existed — so an older server, or a row the second layer did not
 * classify, reads exactly as it did, and no reader needs a third branch.
 */
export const loadMode = z.enum([
    "total",
    "per_hand",
    "bodyweight_plus",
    "assisted",
]);
/**
 * Whether the sides work together or in turn (`D232`): `unilateral` is one
 * side and then the other — a one-arm row, a lunge — and its reps are logged
 * PER SIDE; `bilateral` both at once. Absent or `null` is unknown, which
 * every reader treats as before. An OUTPUT field only, and optional
 * everywhere it appears, so an older app ignores it and survives a newer
 * server (`D172`).
 */
export const laterality = z.enum(["bilateral", "unilateral"]);
/** Which demonstration a clip is. `male` is the canonical thumbnail. */
export const videoGender = z.enum(["male", "female"]);
/**
 * The locales the CATALOGUE is loaded in (`D26`) — not the locales the APP
 * ships. The app's are `en` and `pt-BR`; the catalogue's are `en` and `pt`,
 * because the source data has no regional variants. The mobile client maps one
 * onto the other in `src/lib/catalog/catalog-locale.ts`, and that mapping is
 * the only place the two vocabularies are allowed to meet.
 */
export const catalogueLocale = z.enum(["en", "pt"]);
//# sourceMappingURL=vocabulary.js.map